import React, { useEffect, useState } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import { Container, Card, Row, Col, Spinner, Table, Badge, Button } from "react-bootstrap";
import { getFirestore, collection, getDocs, query, where } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { Pie, Bar } from "react-chartjs-2";
import { FaBox, FaHandHoldingHeart, FaArrowDown, FaArchive, FaChartPie, FaChartBar, FaExclamationTriangle, FaListUl, FaIdCard, FaTools, FaLaptop, FaUndoAlt } from "react-icons/fa";
import { BiPackage, BiTransfer, BiTrendingUp, BiErrorAlt, BiHistory, BiDevices, BiWrench } from "react-icons/bi";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title
);

const db = getFirestore(firebaseApp);

function EstadisticaEPP({ usuario }) {
  const ruc = usuario?.ruc;
  const [loading, setLoading] = useState(true);
  const [totalStock, setTotalStock] = useState(0);
  const [stockPorCategoria, setStockPorCategoria] = useState([]);
  const [eppsMenorStock, setEppsMenorStock] = useState([]);
  const [eppsMasEntregados, setEppsMasEntregados] = useState([]);
  const [movimientosRecientes, setMovimientosRecientes] = useState([]);
  const [totalEntregas, setTotalEntregas] = useState(0);
  const [totalIngresos, setTotalIngresos] = useState(0);
  const [eppsNuncaEntregados, setEppsNuncaEntregados] = useState([]);
  const [eppsPorVencer, setEppsPorVencer] = useState([]);
  
  // Nuevos estados para Activos (Equipos y Herramientas)
  const [statsActivos, setStatsActivos] = useState({
    totalEquipos: 0,
    equiposPrestados: 0,
    totalHerramientas: 0,
    herramientasPrestadas: 0,
  });

  // Intel para toma de decisiones
  const [eppsCriticosForecast, setEppsCriticosForecast] = useState([]); // Forecast de agotamiento
  const [deudoresActivos, setDeudoresActivos] = useState([]); // Trabajadores con mas activos
  const [reporteReposiciones, setReporteReposiciones] = useState({ proximas: 0, dnis: [] }); // Proximos meses
  const [consumoMensualTrend, setConsumoMensualTrend] = useState({ labels: [], data: [] });

  useEffect(() => {
    if (ruc) cargarEstadisticas();
  }, [ruc]);

  const cargarEstadisticas = async () => {
    try {
      setLoading(true);

      // 1. Catálogo de EPPs filtrado por RUC
      const qEpps = query(collection(db, "epp_catalogo"), where("ruc_empresa", "==", ruc));
      const eppsSnap = await getDocs(qEpps);
      
      let total = 0;
      let eppsArray = [];
      let categorias = {};
      
      eppsSnap.forEach(doc => {
        const data = doc.data();
        total += data.stock || 0;
        eppsArray.push({ ...data, id: doc.id });
        if (!categorias[data.categoria]) categorias[data.categoria] = 0;
        categorias[data.categoria] += data.stock || 0;
      });
      
      setTotalStock(total);
      setStockPorCategoria(Object.entries(categorias).map(([cat, stock]) => ({ cat, stock })));

      // EPPs por vencer
      const hoy = new Date();
      const en30dias = new Date();
      en30dias.setDate(hoy.getDate() + 30);
      const porVencer = eppsArray.filter(epp =>
        epp.fechaVencimiento &&
        new Date(epp.fechaVencimiento) >= hoy &&
        new Date(epp.fechaVencimiento) <= en30dias
      );
      setEppsPorVencer(porVencer);

      // 2. Kardex filtrado por RUC
      const qKardex = query(collection(db, "epp_kardex"), where("ruc_empresa", "==", ruc));
      const kardexSnap = await getDocs(qKardex);
      
      const entregas = {};
      const ingresos = {};
      let movimientos = [];
      let entregadosCodigos = new Set();
      let totalEnt = 0;
      let totalIng = 0;

      kardexSnap.forEach(doc => {
        const data = doc.data();
        movimientos.push({ ...data, id: doc.id });
        if (data.tipo === "salida") {
          entregas[data.codigo] = (entregas[data.codigo] || 0) + (data.cantidad || 0);
          entregadosCodigos.add(String(data.codigo));
          totalEnt += data.cantidad || 0;
        }
        if (data.tipo === "ingreso") {
          ingresos[data.codigo] = (ingresos[data.codigo] || 0) + (data.cantidad || 0);
          totalIng += data.cantidad || 0;
        }
      });

      // 3. Menor Stock
      setEppsMenorStock([...eppsArray].sort((a, b) => a.stock - b.stock).slice(0, 5));

      // 4. Más entregados
      const topEntregados = Object.entries(entregas)
        .map(([codigo, cantidad]) => {
          const epp = eppsArray.find(e => String(e.codigo) === String(codigo));
          return { codigo, nombre: epp?.nombre || "Desconocido", cantidad };
        })
        .sort((a, b) => b.cantidad - a.cantidad)
        .slice(0, 5);
      setEppsMasEntregados(topEntregados);

      // 5. Movimientos recientes
      setMovimientosRecientes(movimientos
        .sort((a, b) => (b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha)) - (a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha)))
        .slice(0, 10));

      setTotalEntregas(totalEnt);
      setTotalIngresos(totalIng);

      // 7. Nunca entregados
      setEppsNuncaEntregados(eppsArray.filter(epp => !entregadosCodigos.has(String(epp.codigo))));

      // 8. Cargar Activos (Equipos y Herramientas)
      const rucStr = ruc.toString();
      const qEquipos = query(collection(db, "catalogo_equipos"), where("ruc_empresa", "==", rucStr));
      const qHerramientas = query(collection(db, "herramientas_catalogo"), where("ruc_empresa", "==", rucStr));
      const qPrestamosHist = query(collection(db, "prestamos_historial"), where("ruc_empresa", "==", rucStr));

      const [snapEquipos, snapHerramientas, snapPrestHist] = await Promise.all([
        getDocs(qEquipos), getDocs(qHerramientas), getDocs(qPrestamosHist)
      ]);

      let totalEq = 0, eqPres = 0;
      snapEquipos.forEach(doc => {
        totalEq++;
        if (doc.data().estado !== "Disponible") eqPres++;
      });

      let totalHe = 0, hePres = 0;
      snapHerramientas.forEach(doc => {
        totalHe++;
        // Para herramientas, el "estado" a veces es Disponible/Prestado o se maneja por stock
        if (doc.data().estado !== "Disponible" && doc.data().estado !== undefined) hePres++;
      });

      setStatsActivos({
        totalEquipos: totalEq,
        equiposPrestados: eqPres,
        totalHerramientas: totalHe,
        herramientasPrestadas: hePres
      });

      // 9. Integrar movimientos de préstamos a la tabla reciente
      const prestamosMovs = [];
      const deudoresMap = new Map();
      
      snapEquipos.forEach(doc => {
        const d = doc.data();
        if (d.estado !== "Disponible" && d.trabajadorDni) {
          const dni = d.trabajadorDni.toString();
          const current = deudoresMap.get(dni) || { dni, nombre: d.trabajadorNombre, count: 0 };
          deudoresMap.set(dni, { ...current, count: current.count + 1 });
        }
      });
      snapHerramientas.forEach(doc => {
        const d = doc.data();
        if (d.estado !== "Disponible" && d.trabajadorDni) {
          const dni = d.trabajadorDni.toString();
          const current = deudoresMap.get(dni) || { dni, nombre: d.trabajadorNombre, count: 0 };
          deudoresMap.set(dni, { ...current, count: current.count + 1 });
        }
      });
      setDeudoresActivos(Array.from(deudoresMap.values()).sort((a, b) => b.count - a.count).slice(0, 5));

      snapPrestHist.forEach(doc => {
        const d = doc.data();
        prestamosMovs.push({
          ...d,
          id: doc.id,
          nombre: d.itemNombre || "Item Desconocido",
          tipo: d.tipo || "prestamo", // 'prestamo' o 'devolucion'
          tipoActivo: d.itemTipo || "activo"
        });
      });

      // 10. Forecast de Consumo (Decisiones de Compra)
      const hoyEpoch = hoy.getTime();
      const hace30dias = hoyEpoch - (30 * 24 * 60 * 60 * 1000);
      
      const salidasRecientes = movimientos.filter(m => 
        m.tipo === "salida" && 
        (m.fecha?.toDate ? m.fecha.toDate().getTime() : new Date(m.fecha).getTime()) > hace30dias
      );

      const forecastArr = eppsArray.map(epp => {
        const totalSalidas30d = salidasRecientes
          .filter(s => String(s.codigo) === String(epp.codigo))
          .reduce((acc, curr) => acc + (curr.cantidad || 0), 0);
        
        const consumoDiario = totalSalidas30d / 30;
        const diasRestantes = consumoDiario > 0 ? Math.floor(epp.stock / consumoDiario) : Infinity;
        
        return { ...epp, consumoDiario, diasRestantes };
      }).filter(f => f.diasRestantes <= 15).sort((a,b) => a.diasRestantes - b.diasRestantes);

      setEppsCriticosForecast(forecastArr);

      // 11. Tendencia Mensual (Decisiones de Almacén)
      const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
      const trendData = Array(12).fill(0);
      movimientos.forEach(m => {
        if (m.tipo === "salida") {
          const f = m.fecha?.toDate ? m.fecha.toDate() : new Date(m.fecha);
          if (f.getFullYear() === hoy.getFullYear()) trendData[f.getMonth()] += (m.cantidad || 1);
        }
      });
      setConsumoMensualTrend({ 
        labels: meses, 
        data: trendData 
      });

      const todosMovimientos = [...movimientos, ...prestamosMovs];
      setMovimientosRecientes(todosMovimientos
        .sort((a, b) => (b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha)) - (a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha)))
        .slice(0, 10));

    } catch (error) {
      console.error("Error al cargar estadísticas:", error);
    } finally {
      setLoading(false);
    }
  };

  const pieData = {
    labels: stockPorCategoria.map((c) => c.cat),
    datasets: [{
      data: stockPorCategoria.map((c) => c.stock),
      backgroundColor: ["#f97316", "#fb923c", "#fdba74", "#fed7aa", "#ffedd5", "#ea580c", "#c2410c", "#9a3412"],
      borderWidth: 0
    }],
  };

  const barData = {
    labels: eppsMasEntregados.map((e) => e.nombre),
    datasets: [{
      label: "Cantidad entregada",
      data: eppsMasEntregados.map((e) => e.cantidad),
      backgroundColor: "rgba(249, 115, 22, 0.8)",
      borderRadius: 8,
    }],
  };

  return (
    <div className="min-vh-100 py-4" style={{ backgroundColor: "#f8f9fa" }}>
      <Container fluid className="px-lg-5 px-3">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="warning" size="xl" />
            <p className="mt-3 text-muted fw-bold">Calculando estadísticas para RUC: {ruc}...</p>
          </div>
        ) : (
          <>
            <Card className="shadow-lg border-0 mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              borderBottom: "3px solid #ea580c",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold mb-1">
                  <FaChartPie className="me-3" />
                  Dashboard de Inventario EPP
                </h2>
                <p className="mb-0 opacity-75 text-uppercase small tracking-wider">
                  Análisis operativo para RUC: <strong>{ruc}</strong>
                </p>
              </div>
              <div className="bg-white text-orange p-2 px-3 fw-bold rounded-3 shadow-sm" style={{ color: "#f97316" }}>
                <FaIdCard className="me-2" />
                Vista General
              </div>
            </div>
          </Card.Header>
              <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                <Row className="g-4 mb-4">
                  {[
                    { label: "Total Stock EPP", val: totalStock, sub: "EPPs en almacén", icon: <BiPackage size={30} />, color: "#f97316" },
                    { label: "EPPs Entregados", val: totalEntregas, sub: "Consumo histórico", icon: <BiTransfer size={30} />, color: "#ea580c" },
                    { label: "Equipos en Uso", val: statsActivos.equiposPrestados, sub: `De ${statsActivos.totalEquipos} totales`, icon: <BiDevices size={30} />, color: "#3b82f6" },
                    { label: "Herramientas en Uso", val: statsActivos.herramientasPrestadas, sub: `De ${statsActivos.totalHerramientas} totales`, icon: <BiWrench size={30} />, color: "#10b981" }
                  ].map((card, idx) => (
                    <Col md={3} key={idx}>
                      <Card className="border-0 shadow-sm h-100 transition-hover" style={{ borderRadius: "20px" }}>
                        <Card.Body className="d-flex align-items-center p-4">
                          <div className="rounded-circle p-3 me-3 d-flex align-items-center justify-content-center shadow-sm" style={{ backgroundColor: `${card.color}20`, color: card.color, width: "60px", height: "60px" }}>
                            {card.icon}
                          </div>
                          <div>
                            <div className="text-secondary small fw-bold text-uppercase mb-1">{card.label}</div>
                            <h3 className="fw-bold mb-0" style={{ color: "#334155" }}>{card.val}</h3>
                            <div className="small text-muted opacity-75">{card.sub}</div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Row className="g-4 mb-4">
                  <Col lg={4}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "20px" }}>
                      <Card.Body>
                        <h6 className="fw-bold mb-4 text-secondary text-uppercase d-flex align-items-center">
                          <FaChartPie className="me-2 text-warning" />
                          EPPs Críticos - Pronóstico de Agotamiento
                        </h6>
                        <div className="table-responsive">
                          {eppsCriticosForecast.length > 0 ? (
                            <Table size="sm" className="mb-0">
                              <tbody>
                                {eppsCriticosForecast.slice(0, 4).map(epp => (
                                  <tr key={epp.id}>
                                    <td className="ps-0 border-0">
                                      <div className="small fw-bold">{epp.nombre}</div>
                                      <div className="x-small text-muted">{epp.stock} unidades en stock</div>
                                    </td>
                                    <td className="text-end border-0">
                                      <Badge bg="danger" className="p-2">
                                        {epp.diasRestantes} días
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <div className="text-center py-5 text-muted small">Sin alertas de agotamiento inminente.</div>
                          )}
                        </div>
                        <div className="mt-3 p-3 bg-light rounded-3 x-small text-muted">
                          * Calculado basado en la tasa de consumo de los últimos 30 días.
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={8}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "20px" }}>
                      <Card.Body>
                        <h6 className="fw-bold mb-4 text-secondary text-uppercase d-flex align-items-center">
                          <FaChartBar className="me-2 text-orange" />
                          Consumo Mensual de EPP ({new Date().getFullYear()})
                        </h6>
                        <div style={{ height: "300px" }}>
                          <Bar 
                            data={{
                              labels: consumoMensualTrend.labels,
                              datasets: [{
                                label: "Salidas EPP",
                                data: consumoMensualTrend.data,
                                backgroundColor: "rgba(249, 115, 22, 0.7)",
                                borderRadius: 5
                              }]
                            }} 
                            options={{ 
                              maintainAspectRatio: false, 
                              plugins: { legend: { display: false } },
                              scales: { y: { beginAtZero: true, grid: { color: "#f1f5f9" } }, x: { grid: { display: false } } }
                            }} 
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="g-4 mb-4">
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100 bg-white" style={{ borderRadius: "20px" }}>
                      <Card.Body>
                        <h6 className="fw-bold mb-4 text-primary text-uppercase d-flex align-items-center">
                          <FaIdCard className="me-2" />
                          Ranking de Trabajadores con Activos Pendientes
                        </h6>
                        <div className="table-responsive">
                           {deudoresActivos.length > 0 ? (
                             <Table hover className="align-middle mb-0">
                                <thead>
                                  <tr className="text-muted small">
                                    <th>COLABORADOR</th>
                                    <th className="text-center">ÍTEMS EN PRÉSTAMO</th>
                                    <th className="text-center">ACCIONES</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {deudoresActivos.map(d => (
                                    <tr key={d.dni}>
                                      <td>
                                        <div className="fw-bold small">{d.nombre}</div>
                                        <div className="x-small text-muted">DNI: {d.dni}</div>
                                      </td>
                                      <td className="text-center">
                                         <Badge bg="info" className="p-2 fs-6" style={{ minWidth: "35px" }}>{d.count}</Badge>
                                      </td>
                                      <td className="text-center">
                                         <Button variant="outline-primary" size="sm" className="rounded-pill px-3">Notificar</Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                             </Table>
                           ) : (
                             <div className="text-center py-5 text-muted small">No hay herramientas fuera de almacén.</div>
                           )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "20px" }}>
                      <Card.Body>
                        <h6 className="fw-bold mb-4 text-warning text-uppercase d-flex align-items-center">
                          <i className="fas fa-shopping-cart me-2"></i>
                          Módulo de Decisiones de Compra
                        </h6>
                        <div className="d-flex flex-column gap-3">
                          {eppsCriticosForecast.length > 0 ? (
                            <div className="p-3 border-orange border-start border-4 bg-orange-light rounded-3" style={{ backgroundColor: "rgba(249, 115, 22, 0.05)" }}>
                              <h6 className="fw-bold text-orange mb-1">¡Alerta de Adquisición Próxima!</h6>
                              <p className="small mb-0 text-dark">
                                Basado en el consumo, necesitas comprar <strong>{eppsCriticosForecast[0].nombre}</strong> en menos de <strong>{eppsCriticosForecast[0].diasRestantes} días</strong>.
                              </p>
                            </div>
                          ) : null}

                          <div className="p-3 border-info border-start border-4 bg-light rounded-3">
                            <h6 className="fw-bold text-info mb-1">Auditoría de Devoluciones</h6>
                            <p className="small mb-0 text-dark text-muted">
                              Hay {statsActivos.equiposPrestados + statsActivos.herramientasPrestadas} activos en campo. 
                              Optimiza el stock rotando los equipos con más de 15 días en préstamo.
                            </p>
                          </div>
                          
                          <div className="p-3 border-success border-start border-4 bg-success-light rounded-3" style={{ backgroundColor: "rgba(16, 185, 129, 0.05)" }}>
                            <h6 className="fw-bold text-success mb-1">Estado de Reposiciones</h6>
                            <p className="small mb-0 text-dark">
                              El sistema estima que no hay entregas masivas programadas para el próximo mes según el Kardex histórico.
                            </p>
                          </div>
                        </div>
                        <Button variant="warning" className="w-100 mt-4 text-white fw-bold py-2 rounded-3 shadow-sm">
                          DESCARGAR REPORTE DE DECISIONES (PDF)
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="g-4">
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "20px" }}>
                      <Card.Body>
                        <h6 className="fw-bold mb-4 text-danger text-uppercase d-flex align-items-center">
                          <BiErrorAlt className="me-2" size={20} />
                          Alertas de Stock Crítico
                        </h6>
                        <div className="table-responsive">
                          <Table hover className="align-middle mb-0">
                            <thead className="bg-light small fw-bold text-muted">
                              <tr>
                                <th className="border-0 py-3 pe-0">ARTÍCULO</th>
                                <th className="border-0 py-3 text-center pe-0">STOCK</th>
                                <th className="border-0 py-3 text-center ps-0">ESTADO</th>
                              </tr>
                            </thead>
                            <tbody>
                              {eppsMenorStock.map(epp => (
                                <tr key={epp.id}>
                                  <td className="py-3 border-0">
                                    <div className="fw-bold text-dark small">{epp.nombre}</div>
                                    <small className="text-muted">{epp.categoria}</small>
                                  </td>
                                  <td className="py-3 text-center border-0">
                                    <span className={`fw-bold ${epp.stock <= 5 ? "text-danger" : "text-warning"}`}>{epp.stock}</span>
                                  </td>
                                  <td className="py-3 text-center border-0">
                                    <Badge bg={epp.stock <= 5 ? "danger" : "warning"} pill className="px-2" style={{ fontSize: "0.7rem" }}>
                                      {epp.stock <= 5 ? "CRÍTICO" : "BAJO"}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "20px" }}>
                      <Card.Body>
                        <h6 className="fw-bold mb-4 text-secondary text-uppercase d-flex align-items-center">
                          <BiHistory className="me-2 text-secondary" size={20} />
                          Últimos Movimientos de Kardex
                        </h6>
                        <div className="table-responsive">
                          <Table hover className="align-middle mb-0">
                            <thead className="small fw-bold text-muted bg-light">
                              <tr>
                                <th className="border-0 py-3 pe-0">FECHA</th>
                                <th className="border-0 py-3 text-center pe-0">TIPO</th>
                                <th className="border-0 py-3 pe-0">REFERENCIA</th>
                                <th className="border-0 py-3 text-center pe-0">CANT</th>
                              </tr>
                            </thead>
                            <tbody className="small">
                              {movimientosRecientes.map(mov => (
                                <tr key={mov.id}>
                                  <td className="py-3 border-0 text-muted">{mov.fecha?.toDate ? mov.fecha.toDate().toLocaleDateString() : (mov.fecha instanceof Date ? mov.fecha.toLocaleDateString() : "-")}</td>
                                  <td className="py-3 text-center border-0">
                                    <Badge 
                                      bg={
                                        mov.tipo === "ingreso" || mov.tipo === "devolucion" ? "success" : 
                                        mov.tipo === "salida" ? "orange" : "info"
                                      } 
                                      style={mov.tipo === "salida" ? { backgroundColor: "#f97316" } : {}} 
                                      className="px-2" 
                                      pill
                                    >
                                      {mov.tipo.toUpperCase()}
                                    </Badge>
                                  </td>
                                  <td className="py-3 border-0 fw-bold text-dark">
                                    {mov.nombre}
                                    {mov.workerNombre && <div className="small text-muted fw-normal">{mov.workerNombre}</div>}
                                  </td>
                                  <td className="py-3 text-center border-0 fw-bold">{mov.cantidad || 1}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </div>
  );
}

export default EstadisticaEPP;